/**
 * Domínios autorizados e allowlist de saída.
 *
 * Valida com `isValidDomainEntry` do engine — a mesma função que o backend usa para decidir o `403`.
 * Um domínio aceito aqui e recusado lá seria a pior UX possível.
 *
 * As duas listas ficam em cards separados porque são **entradas opostas**: uma diz quem pode carregar
 * o widget, a outra diz para onde o servidor pode ligar. Empilhadas com um `border-t` entre elas —
 * como estavam — o aviso de uma parecia valer para a outra.
 */

import { useState } from 'react';
import { isValidDomainEntry, matchesDomain } from '@merlin/flow-engine';
import { useEditor } from '../../store/editor.js';
import { Badge, Field, Input, Notice, Section, StringList } from '../ui.js';
import { IconeDominios, IconeWebhookBackend } from '../icons.js';
import { SeloPropagacao, ValeAoSalvar } from './Propagacao.js';

export function DomainsSection() {
  const authorizedDomains = useEditor((state) => state.authorizedDomains);
  const setAuthorizedDomains = useEditor((state) => state.setAuthorizedDomains);
  const outboundAllowlist = useEditor((state) => state.outboundAllowlist);
  const setOutboundAllowlist = useEditor((state) => state.setOutboundAllowlist);
  const [probe, setProbe] = useState('');

  const validateDomain = (value: string): string | null => {
    if (!value.trim()) return 'Informe um domínio.';
    return isValidDomainEntry(value)
      ? null
      : 'Formato inválido. Use cliente.com, *.cliente.com ou localhost — "*" solto não é aceito.';
  };

  const autorizado = probe.trim() ? matchesDomain(probe, authorizedDomains) : null;

  return (
    <div className="space-y-6">
      <Section
        icon={IconeDominios}
        title="Domínios autorizados"
        description="A lista que libera o widget. Fora dela, o servidor nem entrega a configuração."
        action={<SeloPropagacao versionado={false} />}
      >
        <Notice tone="warn" title="É esta lista que libera o widget">
          <p>
            Fora dela, o servidor responde <strong>403 e não entrega a config</strong> — o widget nem
            carrega. É também o que autoriza o modo <code>page</code> do bloco de código.
          </p>
          <p>
            <code>*.cliente.com</code> cobre um nível de subdomínio e <strong>não</strong> cobre o
            ápice: para valer nos dois, liste <code>cliente.com</code> também.
          </p>
        </Notice>

        {/*
          O aviso que faltava. Sem ele, quem acrescenta um domínio procura o botão Publicar, encontra
          apagado e conclui que a alteração não foi salva — foi o relato que originou isto.
        */}
        <Notice tone="ok" title="Domínio vale sem publicar">
          <ValeAoSalvar oQue="A lista de domínios" />
        </Notice>

        <StringList
          values={authorizedDomains}
          onChange={setAuthorizedDomains}
          placeholder="cliente.com"
          validate={validateDomain}
          addLabel="Adicionar domínio"
        />

        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <Field label="Testar um endereço" hint="Usa exatamente a mesma regra do servidor.">
            <Input
              value={probe}
              placeholder="www.cliente.com"
              onChange={(event) => setProbe(event.target.value)}
            />
          </Field>
          {autorizado !== null ? (
            <p aria-live="polite">
              {autorizado ? (
                <Badge tone="ok">autorizado — o widget carrega</Badge>
              ) : (
                <Badge tone="danger">bloqueado — o servidor responderia 403</Badge>
              )}
            </p>
          ) : null}
        </div>
      </Section>

      <Section
        icon={IconeWebhookBackend}
        title="Allowlist de saída"
        description="Para onde o webhook de backend pode ligar quando o host da URL vem de uma variável."
        action={<SeloPropagacao versionado={false} />}
      >
        <Notice tone="info">
          Só é obrigatória quando a URL do webhook de backend tem variável na parte do host. Com host
          fixo, o próprio host já vale como decisão sua. Redes internas são sempre bloqueadas,
          independente do que estiver aqui.
        </Notice>
        <StringList
          values={outboundAllowlist}
          onChange={setOutboundAllowlist}
          placeholder="api.crm.com"
          validate={(value) => (value.trim() ? validateDomain(value) : 'Informe um domínio.')}
          addLabel="Adicionar destino"
        />
      </Section>
    </div>
  );
}
